export type Plan = 'free' | 'starter' | 'pro' | 'enterprise' | 'custom'

export interface CustomPlanLimits {
  siteLimit: number
  pageLimit: number
  postLimit: number
  canViewAnalytics: boolean
  canUseAiAssistant: boolean
  canUseProBlocks: boolean
}

export type UserRole = 'owner' | 'admin' | 'editor' | 'author' | 'member'

export interface Tenant {
  id: string
  name: string
  slug: string
  plan: Plan
  customLimits: CustomPlanLimits | null
  createdAt: string
  updatedAt: string
}

// Fail-safe limits for plan === 'custom' with no custom_limits row set yet
// (should not happen via set_tenant_custom_plan, but a NULL here must not
// silently grant unlimited access) — mirrors the 'free' tier exactly.
export const FREE_PLAN_LIMITS: CustomPlanLimits = {
  siteLimit: 1,
  pageLimit: 5,
  postLimit: 5,
  canViewAnalytics: false,
  canUseAiAssistant: false,
  canUseProBlocks: false,
}

export function tenantCanViewAnalytics(tenant: Pick<Tenant, 'plan' | 'customLimits'>): boolean {
  if (tenant.plan === 'custom') return (tenant.customLimits ?? FREE_PLAN_LIMITS).canViewAnalytics
  return tenant.plan !== 'free'
}

export function tenantCanUseAiAssistant(tenant: Pick<Tenant, 'plan' | 'customLimits'>): boolean {
  if (tenant.plan === 'custom') return (tenant.customLimits ?? FREE_PLAN_LIMITS).canUseAiAssistant
  return tenant.plan === 'pro' || tenant.plan === 'enterprise'
}

// Gates the product-catalog block (BlockPalette.tsx) — same tier semantics
// as canUseAiAssistant in the 4 fixed plans (pro+), but independently
// configurable for a negotiated custom plan.
export function tenantCanUseProBlocks(tenant: Pick<Tenant, 'plan' | 'customLimits'>): boolean {
  if (tenant.plan === 'custom') return (tenant.customLimits ?? FREE_PLAN_LIMITS).canUseProBlocks
  return tenant.plan === 'pro' || tenant.plan === 'enterprise'
}

export interface TenantMember {
  id: string
  tenantId: string
  userId: string
  role: UserRole
  name: string | null
  email: string
  createdAt: string
}

export type InviteRole = Exclude<UserRole, 'owner'>

export interface TenantInvite {
  id: string
  tenantId: string
  email: string
  role: InviteRole
  expiresAt: string
  createdAt: string
}
